/**
 * Admin Trial Management Page
 * Interface for managing trial accounts, activations, and conversions
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import { 
  adminTrialManagementService, 
  TrialAccount, 
  TrialStatus, 
  TrialAnalytics,
  TrialFilters 
} from '../services/admin-trial-management.service';

const AdminTrialManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<TrialAccount[]>([]);
  const [analytics, setAnalytics] = useState<TrialAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'all' | 'pending' | 'active' | 'expired' | 'full-pending'>('overview');
  const [filters, setFilters] = useState<TrialFilters>({
    page: 1,
    limit: 20
  });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load analytics for overview tab
      if (activeTab === 'overview') {
        const analyticsData = await adminTrialManagementService.getTrialAnalytics();
        setAnalytics(analyticsData);
      }

      // Load accounts based on active tab
      let accountFilters: TrialFilters = { ...filters };
      
      switch (activeTab) {
        case 'pending':
          accountFilters.status = TrialStatus.PENDING_TRIAL_APPROVAL;
          break;
        case 'active':
          accountFilters.status = TrialStatus.TRIAL_ACTIVE;
          break;
        case 'expired':
          accountFilters.status = TrialStatus.TRIAL_EXPIRED;
          break;
        case 'full-pending':
          accountFilters.status = TrialStatus.FULL_REGISTRATION_PENDING_APPROVAL;
          break;
        default:
          // For 'all' and 'overview', don't filter by status
          break;
      }

      const result = await adminTrialManagementService.getTrialAccounts(accountFilters);
      setAccounts(result.accounts);
      setTotal(result.total);
      setTotalPages(result.totalPages);

    } catch (err: any) {
      console.error('Error loading trial management data:', err);
      setError(err.response?.data?.error || 'Failed to load trial management data');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateTrial = async (id: string) => {
    if (!confirm('Are you sure you want to activate this trial account?')) return;

    try {
      await adminTrialManagementService.activateTrial(id);
      await loadData();
    } catch (err: any) {
      alert('Failed to activate trial: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRevokeTrial = async (id: string) => {
    const reason = prompt('Enter revocation reason:');
    if (!reason) return;

    try {
      await adminTrialManagementService.revokeTrial(id, reason);
      await loadData();
    } catch (err: any) {
      alert('Failed to revoke trial: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleApproveFullRegistration = async (id: string) => {
    if (!confirm('Are you sure you want to approve full registration for this account?')) return;

    try {
      await adminTrialManagementService.approveFullRegistration(id);
      await loadData();
    } catch (err: any) {
      alert('Failed to approve full registration: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRejectFullRegistration = async (id: string) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await adminTrialManagementService.rejectFullRegistration(id, reason);
      await loadData();
    } catch (err: any) {
      alert('Failed to reject full registration: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleProcessExpiredTrials = async () => {
    if (!confirm('Are you sure you want to process all expired trials?')) return;

    try {
      const result = await adminTrialManagementService.processExpiredTrials();
      alert(`Successfully processed ${result.processedCount} expired trials`);
      await loadData();
    } catch (err: any) {
      alert('Failed to process expired trials: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  if (loading && activeTab === 'overview') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading trial management data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={loadData}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Trial Management</h1>
            <p className="text-sm text-gray-600 mt-1">Manage trial accounts and conversions</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back to Admin Dashboard
            </Button>
            <Button onClick={handleProcessExpiredTrials}>
              Process Expired Trials
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px" role="tablist" aria-label="Trial management sections">
              {[
                { key: 'overview', label: 'Overview', count: null },
                { key: 'all', label: 'All Trials', count: total },
                { key: 'pending', label: 'Pending Approval', count: null },
                { key: 'active', label: 'Active Trials', count: null },
                { key: 'expired', label: 'Expired', count: null },
                { key: 'full-pending', label: 'Full Registration Pending', count: null }
              ].map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key as any)}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  className={`px-6 py-3 text-sm font-medium border-b-2 ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  {tab.count !== null && ` (${tab.count})`}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard 
                title="Total Trials" 
                value={analytics.totalTrials} 
                icon="📊" 
                color="blue" 
              />
              <StatCard 
                title="Active Trials" 
                value={analytics.activeTrials} 
                icon="🟢" 
                color="green" 
              />
              <StatCard 
                title="Expired Trials" 
                value={analytics.expiredTrials} 
                icon="⏰" 
                color="gray" 
              />
              <StatCard 
                title="Conversion Rate" 
                value={`${analytics.conversionRate.toFixed(1)}%`} 
                icon="📈" 
                color="purple" 
              />
            </div>

            {/* Additional Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Budget Usage</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {adminTrialManagementService.formatCurrency(analytics.averageBudgetUsage)}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Operations</h3>
                <div className="space-y-2">
                  {analytics.topOperations.slice(0, 5).map((op, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{op.operationType}</span>
                      <div className="text-right">
                        <span className="text-sm font-medium">{op.count} uses</span>
                        <br />
                        <span className="text-xs text-gray-500">
                          {adminTrialManagementService.formatCurrency(op.totalCost)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Accounts List */}
        {activeTab !== 'overview' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex gap-4">
                <input
                  type="text"
                  placeholder="Search by manufacturer name or email..."
                  aria-label="Search trial accounts"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <Button onClick={loadData} disabled={loading}>
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </div>

            {/* Accounts Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200" aria-label="Trial accounts">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Manufacturer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Budget Usage
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days Remaining
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accounts.map((account) => (
                    <TrialAccountRow
                      key={account.id}
                      account={account}
                      onActivate={() => handleActivateTrial(account.id)}
                      onRevoke={() => handleRevokeTrial(account.id)}
                      onApproveFullRegistration={() => handleApproveFullRegistration(account.id)}
                      onRejectFullRegistration={() => handleRejectFullRegistration(account.id)}
                    />
                  ))}
                </tbody>
              </table>

              {accounts.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No trial accounts found</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-700">
                    Showing {((filters.page || 1) - 1) * (filters.limit || 20) + 1} to{' '}
                    {Math.min((filters.page || 1) * (filters.limit || 20), total)} of {total} results
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange((filters.page || 1) - 1)}
                      disabled={(filters.page || 1) <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange((filters.page || 1) + 1)}
                      disabled={(filters.page || 1) >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

// Trial Account Row Component
interface TrialAccountRowProps {
  account: TrialAccount;
  onActivate: () => void;
  onRevoke: () => void;
  onApproveFullRegistration: () => void;
  onRejectFullRegistration: () => void;
}

const TrialAccountRow: React.FC<TrialAccountRowProps> = ({
  account,
  onActivate,
  onRevoke,
  onApproveFullRegistration,
  onRejectFullRegistration
}) => {
  const daysRemaining = adminTrialManagementService.calculateDaysRemaining(account.endDate);
  const budgetUsagePercentage = adminTrialManagementService.calculateBudgetUsagePercentage(
    account.budgetUsed,
    account.budgetAllocated
  );

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">{account.manufacturer.name}</div>
          <div className="text-sm text-gray-500">{account.manufacturer.contactEmail}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          adminTrialManagementService.getStatusColorClass(account.status)
        }`}>
          {adminTrialManagementService.getStatusDisplayText(account.status)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {adminTrialManagementService.formatCurrency(account.budgetUsed)} / {adminTrialManagementService.formatCurrency(account.budgetAllocated)}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
          <div 
            className="bg-blue-600 h-2 rounded-full" 
            style={{ width: `${Math.min(budgetUsagePercentage, 100)}%` }}
          ></div>
        </div>
        <div className="text-xs text-gray-500 mt-1">{budgetUsagePercentage}% used</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {daysRemaining !== null ? (
          <span className={daysRemaining <= 3 ? 'text-red-600 font-medium' : ''}>
            {daysRemaining} days
          </span>
        ) : (
          'N/A'
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(account.createdAt).toLocaleDateString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex gap-2">
          {account.status === TrialStatus.PENDING_TRIAL_APPROVAL && (
            <>
              <Button size="sm" onClick={onActivate}>
                Activate
              </Button>
              <Button size="sm" variant="outline" onClick={onRevoke}>
                Reject
              </Button>
            </>
          )}
          
          {account.status === TrialStatus.TRIAL_ACTIVE && (
            <Button size="sm" variant="outline" onClick={onRevoke}>
              Revoke
            </Button>
          )}
          
          {account.status === TrialStatus.FULL_REGISTRATION_PENDING_APPROVAL && (
            <>
              <Button size="sm" onClick={onApproveFullRegistration}>
                Approve Full
              </Button>
              <Button size="sm" variant="outline" onClick={onRejectFullRegistration}>
                Reject Full
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  color: 'blue' | 'green' | 'gray' | 'purple';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    gray: 'bg-gray-50 text-gray-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <span className={`text-2xl ${colorClasses[color]} p-2 rounded-lg`}>{icon}</span>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
};

export default AdminTrialManagementPage;

